import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';

export default function HomePage({ data = {} }) {
  const navigate = useNavigate();

  return (
    <div className={styles.homeContainer}>

      {/* A concise introduction for prospective supervisors. */}
      <section className={styles.heroSection} aria-labelledby="profile-heading">
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 id="profile-heading" className={styles.name}>
              {data?.profile?.name || 'Jolok Banarjee'}
            </h1>
            <p className={styles.tagline}>Aerospace Engineer & Research Assistant</p>
            <div className={styles.focusTags} aria-label="Research focus">
              <span>Aircraft health monitoring</span><span>UAV systems</span><span>Structural analysis</span>
            </div>
            <p className={styles.summary}>
              I am an aerospace engineer and research assistant at Aviation and Aerospace
              University, Bangladesh, with experience in autonomous UAV systems, structural
              analysis, and AI-based aircraft health monitoring. I led the development of a
              patented Bangla voice-operated UAV (Patent No. 1007044), contributed to
              award-winning supersonic wing research, and received the Best Thesis Award 2025
              for my turbofan health monitoring thesis. My work includes an IEEE-published
              conference paper and a manuscript under review at AIAA’s Journal of Aerospace
              Information Systems. I am seeking funded graduate research opportunities in
              intelligent and reliable aerospace systems.
            </p>

            <div className={styles.actionButtons}>
              <button type="button" onClick={() => navigate('/cv')} className={styles.btnPrimary}>View CV <span aria-hidden="true">↗</span></button>
              <a href="#selected-research" className={styles.btnSecondary}>Explore research <span aria-hidden="true">↓</span></a>
              <a href="mailto:jolok21014026@aaub.edu.bd" className={styles.textLink}>Contact me ↗</a>
            </div>
          </div>
          <figure className={styles.heroImageWrapper}>
            <div className={styles.profileRing}>
              <img src="/assets/home_profile.png" alt="Jolok Banarjee" className={styles.profileImage} fetchPriority="high" />
            </div>
            <figcaption className={styles.photoCaption}>B.Sc. in Aerospace Engineering</figcaption>
          </figure>
        </div>
      </section>

      <section className={styles.proofStrip} aria-label="Research highlights">
        <div><span className={styles.proofLabel}>Best Thesis Award</span><span>2025 · Turbofan prognostics</span></div>
        <div><span className={styles.proofLabel}>Patented UAV system</span><span>Project lead · Patent 1007044</span></div>
        <div><span className={styles.proofLabel}>AIAA recognition</span><span>2025 · 2nd & 3rd place, team category</span></div>
      </section>

      {/* 3. RESEARCH INTERESTS */}
      <section id="selected-research" className={`${styles.sectionWrapper} ${styles.academicSection}`}>
        <h2 className={styles.sectionHeader}>
          Research Interests
        </h2>

        <div className={styles.interestsList}>
          {[
            'UAV Systems and Autonomy',
            'Aircraft Diagnostics and Structural Health Monitoring',
            'Data-driven Manufacturing and Structural Analysis',
            'Physics-Informed Machine Learning',
          ].map((interest) => (
            <span key={interest} className={styles.interestPill}>
              {interest}
            </span>
          ))}
        </div>
      </section>

      {/* 4. PUBLICATIONS — Year after journal */}
      <section className={`${styles.sectionWrapper} ${styles.academicSection}`}>
        <h2 className={styles.sectionHeader}>
          Publications & Manuscripts
        </h2>
        <div className={styles.pubList}>

          <div className={styles.pubCard}>
            <div className={styles.pubNumberBadge}>1</div>
            <div className={styles.pubContent}>
              <h3 className={styles.pubTitle}>A Novel AI-Driven Anomaly Detection Framework for Predictive Maintenance of Aircraft Engines</h3>
              <p className={styles.pubAuthors}>M. H. Ishak, <strong>J. Banarjee</strong>, A. S. Oishy, and S. R. Bakaul</p>
              <p className={styles.pubVenue}>
                <span className={styles.pubJournalName}>11th International Conference on Mechanical Engineering and Aerospace Engineering (MEAE), Beijing, China. IEEE, Scopus</span>
                <span className={styles.pubYearTag}>2025</span>
              </p>
              <div className={styles.pubFooter}>
                <span className={`${styles.pubStatus} ${styles.statusPublished}`}>Published</span>
                <a href="https://doi.org/10.1109/MEAE68077.2025.11557878" className={styles.pubLinkBtn} target="_blank" rel="noreferrer">Read paper ↗</a>
                <button type="button" onClick={() => navigate('/research/ai-anomaly-detection')} className={styles.pubLinkBtn}>View Research →</button>
              </div>
            </div>
          </div>

          <div className={styles.pubCard}>
            <div className={styles.pubNumberBadge}>2</div>
            <div className={styles.pubContent}>
              <h3 className={styles.pubTitle}>Static Structural Analysis and Material Selection of a Supersonic Fighter Aircraft's Wing</h3>
              <p className={styles.pubAuthors}>M. U. Ahmaad, A. Sadi, <strong>J. Banarjee</strong>, A. Israq, and S. R. Bakaul</p>
              <p className={styles.pubVenue}>
                <span className={styles.pubJournalName}>AIAA Student Conference VII (3rd Place in Paper Team Category)</span>
                <span className={styles.pubYearTag}>2025</span>
              </p>
              <div className={styles.pubFooter}>
                <span className={`${styles.pubStatus} ${styles.statusPublished}`}>Published</span>
                <a href="https://doi.org/10.2514/6.2025-106584" className={styles.pubLinkBtn} target="_blank" rel="noreferrer">Read paper ↗</a>
                <button type="button" onClick={() => navigate('/research/supersonic-wing-analysis')} className={styles.pubLinkBtn}>View Research →</button>
              </div>
            </div>
          </div>
          <div className={styles.pubCard}>
            <div className={styles.pubNumberBadge}>3</div>
            <div className={styles.pubContent}>
              <h3 className={styles.pubTitle}>An AI-Driven Integrated Framework for Turbofan Engine Health Monitoring, Fault Diagnosis, and Prognostics</h3>
              <p className={styles.pubAuthors}><strong>J. Banarjee</strong>, M. H. Ishak, and S. R. Bakaul</p>
              <p className={styles.pubVenue}>
                <span className={styles.pubJournalName}>Journal of Aerospace Information Systems, AIAA</span>
                <span className={styles.pubYearTag}>2026</span>
              </p>
              <div className={styles.pubFooter}>
                <span className={`${styles.pubStatus} ${styles.statusSubmitted}`}>Under Review</span>
                <button type="button" onClick={() => navigate('/research/e2e-pdm-framework')} className={styles.pubLinkBtn}>View Research →</button>
              </div>
            </div>
          </div>

          <div className={styles.pubCard}>
            <div className={styles.pubNumberBadge}>4</div>
            <div className={styles.pubContent}>
              <h3 className={styles.pubTitle}>Sustainable Additive Manufacturing using rPET: Extrusion Design, Mechanical Testing, and Data-Driven Prediction</h3>
              <p className={styles.pubAuthors}><strong>J. Banarjee</strong>, H. P. Himel, T. W. Opul, T. Anny, and S. Khan</p>
              <p className={styles.pubVenue}>
                <span className={styles.pubJournalName}>Rapid Prototyping Journal</span>
                <span className={styles.pubYearTag}>2026</span>
              </p>
              <div className={styles.pubFooter}>
                <span className={`${styles.pubStatus} ${styles.statusSubmitted}`}>Under Review</span>
                <span className={styles.pubAwardTag}>⭐ AIAA 2025 - 2nd Place</span>

                <button type="button" onClick={() => navigate('/research/rpet-additive-manufacturing')} className={styles.pubLinkBtn}>View Research →</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. EDUCATION */}
      <section className={`${styles.sectionWrapper} ${styles.academicSection}`}>
        <h2 className={styles.sectionHeader}>Education</h2>
        <div className={styles.eduCard}>
          <div className={styles.eduLogo}>AAUB</div>
          <div className={styles.eduInfo}>
            <h3 className={styles.eduDegree}>B.Sc. in Aerospace Engineering</h3>
            <p className={styles.eduInstitution}>Aviation and Aerospace University, Bangladesh (AAUB)</p>
            <div className={styles.eduMeta}>
              <span className={styles.eduBadge}>CGPA 3.56/4.00</span>
              <span className={styles.eduBadge}>Last Sem 3.88/4.00</span>
              <span className={styles.eduBadgeHighlight}>Best Thesis 2025</span>
              <span className={styles.eduBadge}>Graduated Mar 2026</span>
            </div>
          </div>
        </div>
      </section>

      {/* 6. AWARDS */}
      <section className={styles.sectionWrapper}>
        <h2 className={styles.sectionHeader}>Awards & Recognitions</h2>
        <div className={styles.galleryGrid}>
          <div className={styles.awardCard}>
            <div className={styles.awardImgBox}><img src="/assets/certificates/Best Thesis Award-2025.jpeg" alt="Best Thesis Award 2025" className={styles.imgContain} /></div>
            <div className={styles.awardCaption}>Best Thesis Award 2025 — AAUB</div>
          </div>
          <div className={styles.awardCard}>
            <div className={styles.awardImgBox}><img src="/assets/certificates/Best Aerospace Vechicle Design-2025.jpeg" alt="Best Aerospace Vehicle Design Award 2026" className={styles.imgContain} /></div>
            <div className={styles.awardCaption}>Best Aerospace Vehicle Design Award 2025 — AAUB</div>
          </div>
          <div className={styles.awardCard}>
            <div className={styles.awardImgBox}><img src="assets/home/AIAA_rPET_2nd_Place_2025.png" alt="AIAA 2nd Place Certificate" className={styles.imgContain} /></div>
            <div className={styles.awardCaption}>2nd Place — AIAA Student Conference 2025 (rPET Research)</div>
          </div>
          <div className={styles.awardCard}>
            <div className={styles.awardImgBox}><img src="assets/home/AIAA_Aircraft_Structure_3rd_Place_2025.png" alt="AIAA 3rd Place Certificate" className={styles.imgContain} /></div>
            <div className={styles.awardCaption}>3rd Place — AIAA Student Conference 2025 (Structural Analysis)</div>
          </div>
          <div className={styles.awardCard}>
            <div className={styles.awardImgBox}><img src="/assets/certificates/Nasa_Space_App_Challenge_2023_1st_Runner_Up_certificate.jpg" alt="NASA Space Apps 1st Runner Up 2023" className={styles.imgContain} /></div>
            <div className={styles.awardCaption}>1st Runner Up — NASA Space Apps Challenge Bangladesh 2023 (Rangpur)</div>
          </div>
          <div className={styles.awardCard}>
            <div className={styles.awardImgBox}><img src="/assets/certificates/Kibo_Robot_2nd_Place_2023.jpg" alt="Kibo Robot Programming Challenge 2nd Place 2023" className={styles.imgContain} /></div>
            <div className={styles.awardCaption}>2nd Place — 4th Kibo Robot Programming Challenge 2023 (Bangladesh Region)</div>
          </div>
        </div>
        <div className={styles.sectionFooter}>
          <button onClick={() => navigate('/about')} className={styles.btnSecondary}>View Full List of Honors & Awards →</button>
        </div>
      </section>

      {/* 7. CORE COMPETENCIES */}
      <section className={styles.sectionWrapper}>
        <h2 className={styles.sectionHeader}>Technical Skills</h2>
        <div className={styles.skillsGrid}>
          <div className={styles.skillCategoryCard}>
            <div className={styles.skillCategoryHeader}><h3>AI & Machine Learning</h3></div>
            <div className={styles.tagsWithLogos}>
              <span className={styles.skillPill}><img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg" alt="Python" className={styles.skillIcon} /> Python</span>
              <span className={styles.skillPill}><img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg" alt="TensorFlow" className={styles.skillIcon} /> TensorFlow / Keras</span>
              <span className={styles.skillPill}><img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/pytorch/pytorch-original.svg" alt="PyTorch" className={styles.skillIcon} /> PyTorch</span>
              <span className={styles.skillPill}><img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/scikitlearn/scikitlearn-original.svg" alt="Scikit-Learn" className={styles.skillIcon} /> Scikit-learn</span>
              <span className={styles.skillPill}><img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/xamarin/xamarin-original.svg" alt="XGBoost" className={styles.skillIcon} /> XGBoost</span>
              <span className={styles.skillPill}><img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" alt="React" className={styles.skillIcon} /> React & FastAPI</span>
            </div>
          </div>
          <div className={styles.skillCategoryCard}>
            <div className={styles.skillCategoryHeader}><h3>Aerospace & Simulation</h3></div>
            <div className={styles.tagsWithLogos}>
              <span className={styles.skillPill}><img src="https://upload.wikimedia.org/wikipedia/commons/2/21/Matlab_Logo.png" alt="MATLAB" className={styles.skillIcon} /> MATLAB</span>
              <span className={styles.skillPill}><img src="https://upload.wikimedia.org/wikipedia/commons/e/e5/ANSYS_logo.png" className={styles.skillIcon} alt="ANSYS" /> ANSYS (FEA & CFD)</span>
              <span className={styles.skillPill}><img src="https://upload.wikimedia.org/wikipedia/en/d/d2/SolidWorks_Logo.svg" className={styles.skillIcon} alt="SolidWorks" /> SolidWorks</span>
              <span className={styles.skillPill}><img src="https://upload.wikimedia.org/wikipedia/commons/1/1f/ArduPilot_logo.svg" className={styles.skillIcon} alt="ArduPilot" /> ArduPilot</span>
              <span className={styles.skillPill}><img src="https://upload.wikimedia.org/wikipedia/commons/8/87/Arduino_Logo.svg" className={styles.skillIcon} alt="Arduino" /> Arduino</span>
              <span className={styles.skillPill}><img src="https://upload.wikimedia.org/wikipedia/fr/3/3b/Raspberry_Pi_logo.svg" className={styles.skillIcon} alt="Raspberry Pi" /> Raspberry Pi</span>
            </div>
          </div>
        </div>
      </section>

      {/* 8. FEATURED DESIGN REPORTS */}
      <section className={styles.sectionWrapper}>
        <h2 className={styles.sectionHeader}>Featured Design Reports</h2>
       
        <div className={styles.featuredGrid}>
          <div className={styles.featuredCard}>
            <div className={styles.featuredImageWrap}>
              <img src="assets/featured/skybee-cover.png" alt="SkyBee UCAV Design Report" className={styles.featuredImage} loading="lazy" onError={(e) => e.target.style.display = 'none'} />
              <div className={styles.featuredOverlay}>
                <span className={styles.featuredTag}>AIAA 2025</span>
                <span className={styles.featuredTagOutline}>Aircraft Design</span>
              </div>
            </div>
            <div className={styles.featuredContent}>
              <h3>SkyBee: Homeland Defense Interceptor</h3>
              <p>Complete AIAA 2024-2025 Aircraft Design project. 3 published papers including 3rd Place Team Category winner. Mach 1.6 UCAV with stealth-optimized airframe.</p>
              <a href="https://shorturl.at/glSOb" target="_blank" rel="noreferrer" className={styles.featuredLink}>🔗 LinkedIn</a>
              <a href="/projects/skybee-homeland-defense-interceptor/" className={styles.featuredLink}>🔗 Project Page</a>
            </div>
          </div>
          <div className={styles.featuredCard}>
            <div className={styles.featuredImageWrap}>
              <img src="assets/featured/modal-analysis-cover.png" alt="Modal Analysis FEA Report" className={styles.featuredImage} loading="lazy" onError={(e) => e.target.style.display = 'none'} />
              <div className={styles.featuredOverlay}>
                <span className={styles.featuredTag}>FEA / ANSYS</span>
                <span className={styles.featuredTagOutline}>Structural Dynamics</span>
              </div>
            </div>
            <div className={styles.featuredContent}>
              <h3>Modal Analysis & Mesh Independence</h3>
              <p>Titanium cantilever beam vibration analysis using Euler-Bernoulli theory and ANSYS Workbench. Achieved 0.04% frequency error margin with 1 mm mesh validation.</p>
              <a href="https://shorturl.at/P6U5u" target="_blank" rel="noreferrer" className={styles.featuredLink}>🔗 LinkedIn + Report</a>
            </div>
          </div>
          <div className={styles.featuredCard}>
            <div className={styles.featuredImageWrap}>
              <img src="assets/featured/sky-sentinel-s26-cover.png" alt="Sky Sentinel S26 Interceptor" className={styles.featuredImage} loading="lazy" onError={(e) => e.target.style.display = 'none'} />
              <div className={styles.featuredOverlay}>
                <span className={styles.featuredTag} style={{ background: 'rgba(250, 204, 21, 0.9)', color: '#92400e' }}>🏆 Best Design 2025</span>
                <span className={styles.featuredTagOutline}>Aerodynamics</span>
              </div>
            </div>
            <div className={styles.featuredContent}>
              <h3>Sky Sentinel S26: Air Defense Interceptor</h3>
              <p>Next-generation supersonic interceptor. Mach 2.2 dash, 750nm combat radius, 22°/sec turn rate. Awarded Best Aerospace Vehicle Design 2025.</p>
              <a href="https://shorturl.at/iWWk5" target="_blank" rel="noreferrer" className={styles.featuredLink}>🔗 LinkedIn + Report</a>
            </div>
          </div>
        </div>
      </section>

      {/* 9. RESEARCH & LEADERSHIP EXPERIENCE */}
      <section className={styles.sectionWrapper}>
        <h2 className={styles.sectionHeader}>Research & Leadership Experience</h2>
        <div className={styles.highlightGrid}>

          <div className={styles.expCard}>
            
            <div className={styles.expCardContent}>
              <div className={styles.expCardHeader}>
                <h3>End-to-End Predictive Maintenance for Turbofan Engines</h3>
                <span className={styles.expBadge} style={{ background: 'rgba(250, 204, 21, 0.15)', color: '#d97706' }}>🏆 Best Thesis Award 2025</span>
              </div>
              <p className={styles.expDesc}>Developed an integrated ML framework combining Bi-LSTM, Transformers, and ensemble methods with Bayesian hyperparameter tuning. Built SHAP-explainable real-time APIs using FastAPI and Streamlit for fleet-wide health monitoring. Manuscript under review at Journal of Aerospace Information Systems, AIAA.</p>
              <button onClick={() => navigate('/research/e2e-pdm-framework')} className={styles.expLink}>Read Methodology →</button>
            </div>
          </div>

          <div className={styles.expCard}>
            
            <div className={styles.expCardContent}>
              <div className={styles.expCardHeader}>
                <h3>Sustainable Additive Manufacturing using Recycled PET</h3>
                <span className={styles.expBadge} style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#047857' }}>🥈 AIAA 2025 — 2nd Place</span>
              </div>
              <p className={styles.expDesc}>Designed Arduino-controlled extrusion system to transform recycled PET into structural 3D printing filament. Conducted ASTM D638 tensile testing across 45 specimens, achieving 47 MPa tensile strength comparable to virgin PLA. Manuscript under review at Rapid Prototyping Journal.</p>
              <button onClick={() => navigate('/research/rpet-additive-manufacturing')} className={styles.expLink}>View Research →</button>
            </div>
          </div>

          <div className={styles.expCard}>
            
            <div className={styles.expCardContent}>
              <div className={styles.expCardHeader}>
                <h3>Bangla Voice-Operated Autonomous UAV</h3>
                <span className={styles.expBadge} style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#2563eb' }}>💡 Patent No. 1007044 (Issued Mar 2026)</span>
              </div>
              <p className={styles.expDesc}>Led a 10-person team integrating Google Cloud Speech-to-Text with MAVLink via MAVProxy for Bangla voice commands. Achieved 95% recognition accuracy across 50+ outdoor flight tests. Granted patent for emergency-response virtual assistance.</p>
              <button onClick={() => navigate('/projects')} className={styles.expLink}>View Project Details →</button>
            </div>
          </div>

          <div className={styles.expCard}>
            
            <div className={styles.expCardContent}>
              <div className={styles.expCardHeader}>
                <h3>Supersonic Wing Structural Analysis</h3>
                <span className={styles.expBadge} style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#7c3aed' }}>🥉 AIAA 2025 — 3rd Place</span>
              </div>
              <p className={styles.expDesc}>Rigorous FEA-based integrity assessment of a supersonic fighter wing under high-Mach conditions using ANSYS Mechanical. Published at AIAA Student Conference VII with DOI: 10.2514/6.2025-106584.</p>
              <button onClick={() => navigate('/research/supersonic-wing-analysis')} className={styles.expLink}>View Research →</button>
            </div>
          </div>

          <div className={styles.expCard}>
            
            <div className={styles.expCardContent}>
              <div className={styles.expCardHeader}>
                <h3>Frontier Alliance — Founder & Director</h3>
                <span className={styles.expBadge} style={{ background: 'rgba(14, 165, 233, 0.15)', color: '#0284c7' }}>🚀 Research Organization</span>
              </div>
              <p className={styles.expDesc}>Founded a research-focused aerospace organization fostering UAV innovation and technical education. Coordinated cross-functional teams of 10+ members for AIAA and NASA competitions, managing project timelines, deliverables, and peer-review workflows.</p>
              <button onClick={() => navigate('/experience')} className={styles.expLink}>View Leadership →</button>
            </div>
          </div>

          <div className={styles.expCard}>
            
            <div className={styles.expCardContent}>
              <div className={styles.expCardHeader}>
                <h3>Biman Bangladesh Airlines — Industrial Intern</h3>
                <span className={styles.expBadge} style={{ background: 'rgba(4, 120, 87, 0.15)', color: '#047857' }}>✈️ MRO Operations</span>
              </div>
              <p className={styles.expDesc}>Completed intensive 1-month internship across Boeing 787, 777, 737, and Dash 8 maintenance teams. Observed 40+ maintenance operations spanning inspections, diagnostics, and regulatory compliance. Gained firsthand operator perspective on the gap between research prototypes and hangar-floor reality.</p>
              <button onClick={() => navigate('/experience')} className={styles.expLink}>View Experience →</button>
            </div>
          </div>
        </div>
      </section>

      {/* 10. PHD CTA */}
      <section className={styles.sectionWrapper} style={{ marginTop: '2rem', marginBottom: '2rem' }}>
        <div className={styles.ctaBanner}>
          <div className={styles.ctaIcon}>🎓</div>
          <h3 className={styles.ctaTitle}>Seeking Fully Funded PhD Opportunities</h3>
          <p className={styles.ctaText}>I am actively looking for PhD positions in <strong>Intelligent Aerospace Systems</strong>, <strong>Structural Health Monitoring</strong>, and <strong>Autonomous Systems</strong>. If your research group aligns with my work, I would love to discuss potential collaboration and funding opportunities.</p>
          <div className={styles.ctaActions}>
            <a href="mailto:jolok21014026@aaub.edu.bd" className={styles.ctaBtnPrimary}>✉️ Email Me</a>
            <button onClick={() => navigate('/cv')} className={styles.ctaBtnSecondary}>📄 Download Full CV</button>
          </div>
        </div>
      </section>
    </div>
  );
}